import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Table, Input, Select, Tag, Space, Modal, InputNumber, Button, message, Typography, Avatar, Tabs } from 'antd'
import { EditOutlined, InboxOutlined, ExportOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { warehouseApi } from '../../services/api'
import StockInPage from './StockIn'
import StockOutPage from './StockOut'

const { Title } = Typography

const CATEGORIES = ['硬件', '软件', '网络设备', '线材辅料', '服务']
const STOCK_STATUS_MAP = {
  normal: { label: '正常', color: 'green', dot: '🟢' },
  low: { label: '预警', color: 'orange', dot: '🟡' },
  out: { label: '缺货', color: 'red', dot: '🔴' },
}

function InventoryOverview() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, keyword: '', category: '', stockStatus: '' })

  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustTarget, setAdjustTarget] = useState(null)
  const [adjustQty, setAdjustQty] = useState(0)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await warehouseApi.inventory(query)
      setData(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleAdjust = async () => {
    try {
      await warehouseApi.adjustInventory(adjustTarget.id, { quantity: adjustQty })
      message.success('库存已调整')
      setAdjustOpen(false)
      setAdjustTarget(null)
      fetchData()
    } catch (e) {
      message.error(e?.error || '调整失败')
    }
  }

  const columns = [
    {
      title: '图片', key: 'image', width: 56,
      render: (_, r) => r.product?.imageUrl
        ? <Avatar shape="square" size={36} src={r.product.imageUrl} alt={r.product?.name} />
        : <Avatar shape="square" size={36} style={{ backgroundColor: '#f0f0f0', color: '#bbb' }}>{r.product?.name?.[0] || '?'}</Avatar>,
    },
    {
      title: '产品名称', key: 'product', width: 200,
      render: (_, r) => r.product?.name || '—',
    },
    {
      title: 'SKU', key: 'sku', width: 120,
      render: (_, r) => r.product?.sku || '—',
    },
    {
      title: '分类', key: 'category', width: 100,
      render: (_, r) => {
        const colors = { 硬件: 'blue', 软件: 'green', 网络设备: 'orange', 线材辅料: 'purple', 服务: 'cyan' }
        return <Tag color={colors[r.product?.category] || 'default'}>{r.product?.category || '—'}</Tag>
      },
    },
    {
      title: '库存数量', key: 'stock', width: 100,
      render: (_, r) => r.quantity,
    },
    {
      title: '最低库存', key: 'minStock', width: 100,
      render: (_, r) => r.product?.minStock || 0,
    },
    {
      title: '库存状态', key: 'status', width: 100,
      render: (_, r) => {
        const s = STOCK_STATUS_MAP[r.stockStatus] || { label: '未知', color: 'default', dot: '⚪' }
        return <span>{s.dot} <Tag color={s.color} style={{ marginLeft: 4 }}>{s.label}</Tag></span>
      },
    },
    {
      title: '操作', key: 'actions', width: 100,
      render: (_, r) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setAdjustTarget(r)
            setAdjustQty(r.quantity)
            setAdjustOpen(true)
          }}
        >调整</Button>
      ),
    },
  ]

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索产品名称/SKU"
          allowClear
          style={{ width: 220 }}
          onSearch={(v) => setQuery((q) => ({ ...q, keyword: v, page: 1 }))}
        />
        <Select
          placeholder="筛选分类"
          allowClear
          style={{ width: 120 }}
          onChange={(v) => setQuery((q) => ({ ...q, category: v || '', page: 1 }))}
        >
          {CATEGORIES.map((c) => (
            <Select.Option key={c} value={c}>{c}</Select.Option>
          ))}
        </Select>
        <Select
          placeholder="库存状态"
          style={{ width: 120 }}
          value={query.stockStatus || 'all'}
          onChange={(v) => setQuery((q) => ({ ...q, stockStatus: v === 'all' ? '' : v, page: 1 }))}
        >
          <Select.Option value="all">全部</Select.Option>
          <Select.Option value="normal">正常</Select.Option>
          <Select.Option value="low">预警</Select.Option>
          <Select.Option value="out">缺货</Select.Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowClassName={(record) => {
          if (record.stockStatus === 'out') return 'row-out-of-stock'
          if (record.stockStatus === 'low') return 'row-low-stock'
          return ''
        }}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 条库存记录`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />

      <Modal
        title="调整库存"
        open={adjustOpen}
        onCancel={() => { setAdjustOpen(false); setAdjustTarget(null) }}
        onOk={handleAdjust}
        okText="确认调整"
      >
        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 16 }}>
            产品：<strong>{adjustTarget?.product?.name}</strong>　当前库存：{adjustTarget?.quantity}
          </p>
          <div style={{ marginBottom: 12 }}>
            <span>新数量：</span>
            <InputNumber
              style={{ width: 200 }}
              min={0}
              value={adjustQty}
              onChange={(v) => setAdjustQty(v)}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default function Warehouse() {
  const [activeTab, setActiveTab] = useState('inventory');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleTabChange = (key) => {
    setActiveTab(key);
    // 切换到库存总览时自动刷新数据（通过 key 变化触发组件 remount）
    if (key === 'inventory') {
      setRefreshKey((k) => k + 1);
    }
  };

  const tabItems = [
    {
      key: 'inventory',
      label: <span><UnorderedListOutlined /> 库存总览</span>,
      children: <InventoryOverview key={refreshKey} />,
    },
    {
      key: 'stockIn',
      label: <span><InboxOutlined /> 入库记录</span>,
      children: <StockInPage />,
    },
    {
      key: 'stockOut',
      label: <span><ExportOutlined /> 出库管理</span>,
      children: <StockOutPage />,
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>仓库管理</Title>
      </div>
      <Tabs defaultActiveKey="inventory" activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
    </div>
  )
}
